// Verwaltung › Unternehmen: Firmendaten für Belege, Erscheinungsbild, Belegtexte, Geschäftstag, Funktionen des
// Branchenprofils, Nummernkreise, Zusatzfelder.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

/** Freiwilliger Text: '' gilt als „nicht angegeben“ (null); undefined = unverändert. */
const zOptionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value === '' ? null : value))

export const zCompanyAddress = z.object({
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
})

export const zCompany = z.object({
  /** Name des Betriebs in der Oberfläche */
  name: z.string(),
  /** Firma laut Register – steht auf Belegen */
  legalName: z.string().nullable(),
  legalForm: z.string().nullable(),
  address: zCompanyAddress,
  phone: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  taxNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  registerCourt: z.string().nullable(),
  registerNumber: z.string().nullable(),
  managingDirectors: z.string().nullable(),
  bankName: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  isSmallBusiness: z.boolean(),
  // ---- E-Rechnung (SPEZIFIKATION §5.4) – landen über den Beleg-Schnappschuss in jeder XRechnung ----
  /** elektronische Adresse des Betriebs (BT-34): E-Mail oder `<Schema>:<Kennung>` (§5.3) */
  einvoiceAddress: z.string().nullable(),
  /** Kontaktstelle für Rechnungen (BT-41), z. B. „Buchhaltung“ oder ein Name */
  contactName: z.string().nullable(),
  /** Gläubiger-Identifikationsnummer für SEPA-Lastschriften (BT-90) */
  creditorId: z.string().nullable(),
  /** eigene GLN (BT-29, Schema 0088) – hilft Betrieben ohne USt-IdNr. und Registereintrag */
  gln: z.string().nullable(),
  /** Erscheinungsbild */
  accentColor: z.string().nullable(),
  accentStyle: z.enum(['mono', 'deep', 'bright']).nullable(),
  logoUrl: z.string().nullable(),
  /** Belegtexte */
  documentHeader: z.string().nullable(),
  documentFooter: z.string().nullable(),
  paymentNote: z.string().nullable(),
  /** Sprache und Geschäftstag */
  locale: z.string(),
  timezone: z.string(),
  currency: z.string(),
  /** Monat, in dem das Geschäftsjahr beginnt (1–12) */
  fiscalYearStartMonth: z.int(),
  /** Uhrzeit 'HH:MM', zu der der Geschäftstag der Filialen endet (Tagesabschluss) */
  businessDayEndsAt: z.string(),
  industryProfile: z.string().nullable(),
  industryProfileName: z.string().nullable(),
  version: z.int(),
})

export const companyGet = defineContract({
  name: 'platform.company.get',
  title: 'Unternehmensdaten anzeigen',
  description: 'Firmendaten für Belege, Erscheinungsbild, Belegtexte und die Einstellungen zu Sprache und Geschäftstag.',
  llm: {
    description:
      'Liefert die Unternehmensdaten des Betriebs: Firma, Anschrift, Steuernummern, Registerangaben, Belegtexte, ' +
      'Geschäftsjahr. Verwenden für Fragen wie „Welche USt-IdNr. steht auf unseren Rechnungen?“. Bankdaten gehören dazu.',
    keywords: ['Firma', 'Anschrift', 'Steuernummer', 'USt-IdNr', 'Impressum', 'Belegtext', 'Geschäftsjahr'],
  },
  input: z.strictObject({}),
  output: zCompany,
  permission: 'platform.settings.read',
  risk: 'read',
})

const zHex = z.string().regex(/^#[0-9a-fA-F]{6}$/, { error: 'Bitte eine Farbe in der Form #rrggbb angeben.' })

export const companyUpdate = defineContract({
  name: 'platform.company.update',
  title: 'Unternehmensdaten ändern',
  description:
    'Ändert Firmendaten, Erscheinungsbild, Belegtexte oder die Einstellungen zum Geschäftstag. Nur die genannten Felder ändern sich; neue Belege tragen die neuen Angaben, festgeschriebene behalten ihre.',
  llm: { description: 'Ändert die Unternehmensdaten. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    name: z.string().trim().min(2, { error: 'Bitte geben Sie den Namen des Betriebs ein.' }).max(120).optional(),
    legalName: zOptionalText(200),
    legalForm: zOptionalText(60),
    street: zOptionalText(200),
    zip: zOptionalText(12),
    city: zOptionalText(120),
    country: zOptionalText(2),
    phone: zOptionalText(60),
    email: zOptionalText(200),
    website: zOptionalText(200),
    taxNumber: zOptionalText(40),
    vatId: zOptionalText(20),
    registerCourt: zOptionalText(120),
    registerNumber: zOptionalText(60),
    managingDirectors: zOptionalText(300),
    bankName: zOptionalText(120),
    iban: zOptionalText(42),
    bic: zOptionalText(11),
    isSmallBusiness: z.boolean().optional(),
    /** E-Rechnung: E-Mail-Adresse oder z. B. 0204:<Leitweg-ID>, 9930:<USt-IdNr.>, 0088:<GLN> */
    einvoiceAddress: zOptionalText(200),
    contactName: zOptionalText(120),
    /** DE + 2 Prüfziffern + 3 Zeichen Geschäftsbereich + 11 Ziffern, z. B. DE98ZZZ09999999999 */
    creditorId: zOptionalText(35),
    /** 13 Ziffern; Leerzeichen zur Gliederung sind erlaubt */
    gln: zOptionalText(20),
    accentColor: zHex.nullable().optional(),
    accentStyle: z.enum(['mono', 'deep', 'bright']).nullable().optional(),
    /** hochgeladenes Logo (POST /api/files, nur Bilder); null entfernt es */
    logoFileId: zId.nullable().optional(),
    documentHeader: zOptionalText(600),
    documentFooter: zOptionalText(600),
    paymentNote: zOptionalText(600),
    fiscalYearStartMonth: z.int().min(1, { error: 'Bitte einen Monat von 1 bis 12 angeben.' }).max(12, { error: 'Bitte einen Monat von 1 bis 12 angeben.' }).optional(),
    businessDayEndsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: 'Bitte eine Uhrzeit in der Form HH:MM angeben.' }).optional(),
    expectedVersion: z.int().positive().optional(),
  }),
  output: zCompany,
  permission: 'platform.settings.manage',
  risk: 'write',
  // Bankverbindung und Steuernummern stehen auf jedem Beleg – wie Rollen und Schlüssel nur mit frischer Anmeldung
  effects: { securitySensitive: true },
  assistant: 'never',
})

// ---- Funktionen des Branchenprofils ---------------------------------------------------------------------------------

export const zFeature = z.object({
  key: z.string(),
  label: z.string(),
  /** ein Satz: was der Schalter ein- bzw. ausblendet */
  description: z.string(),
  enabled: z.boolean(),
  /** Teil des gewählten Branchenprofils */
  inProfile: z.boolean(),
  /** Module, die nur mit diesem Schalter aktiv sind (samt denen, die auf ihnen aufbauen) */
  modules: z.array(z.string()),
  /** Aktionen, die nur mit diesem Schalter erreichbar sind */
  actions: z.int().nonnegative(),
  /** false: Kein Modul und keine Aktion richtet sich bisher nach diesem Schalter – Umschalten ändert noch nichts */
  effective: z.boolean(),
  /** was der Server mit dem Schalter durchsetzt, wo er keine Aktionen ausblendet (Altersprüfung, Fassware, E-Rechnung) */
  effect: z.string().nullable().optional(),
  /** Hinweis, wenn der Schalter mehr verspricht, als gebaut ist („Schulungs- und Demomodus“) */
  note: z.string().nullable(),
})

export const featureList = defineContract({
  name: 'platform.feature.list',
  title: 'Funktionen anzeigen',
  description: 'Die Funktionsschalter des Betriebs mit ihrem Zustand und dem Branchenprofil.',
  llm: {
    description:
      'Liefert die Funktionsschalter des Betriebs (Pfand, Chargen und MHD, Seriennummern, Leihartikel, Kasse …) mit Zustand. ' +
      'Verwenden für „Ist die Chargenführung eingeschaltet?“.',
    keywords: ['Funktion', 'Schalter', 'Branchenprofil', 'Pfand', 'MHD', 'Kasse', 'eingeschaltet'],
  },
  input: z.strictObject({}),
  output: z.object({
    profile: z.object({ key: z.string(), name: z.string(), description: z.string() }).nullable(),
    features: z.array(zFeature),
  }),
  permission: 'platform.settings.read',
  risk: 'read',
})

export const featureSet = defineContract({
  name: 'platform.feature.set',
  title: 'Funktion ein- oder ausschalten',
  description:
    'Schaltet eine Funktion des Branchenprofils für den Betrieb ein oder aus. Ausschalten blendet Bildschirme und Aktionen aus; Daten bleiben erhalten.',
  llm: { description: 'Schaltet eine Funktion ein oder aus. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ key: z.string().min(2).max(60), enabled: z.boolean() }),
  output: z.object({ key: z.string(), enabled: z.boolean() }),
  permission: 'platform.settings.manage',
  risk: 'destructive',
  effects: { reversible: true },
  assistant: 'never',
})

/**
 * Ein Funktionsschalter wurde umgestellt – in der Transaktion des Umschaltens. Module, deren Daten an einem Schalter
 * hängen, prüfen als Abonnent (`onTx`), ob das Ausschalten geht (Altersprüfung nur ohne Artikel mit Altersgrenze,
 * Fassware nur ohne Fass-Gebinde mit Pfand und Leihgebinde) – scheitern sie, schaltet nichts um.
 */
export const featureChanged = defineEvent({
  name: 'platform.feature.changed',
  title: 'Funktion umgeschaltet',
  payload: z.object({ key: z.string(), enabled: z.boolean() }),
})

// ---- Nummernkreise ------------------------------------------------------------------------------------------------------

export const zNumberRange = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  /** Platzhalter {YYYY} {YY} {LOC} {REG} und {000000} für die laufende Nummer */
  pattern: z.string(),
  yearly: z.boolean(),
  perLocation: z.boolean(),
  perRegister: z.boolean(),
  /** an Belegarten gebunden (nicht frei verwendbar) */
  bound: z.boolean(),
  /** begonnene Zähler (je Jahr, Standort, Kasse) */
  counters: z.array(
    z.object({
      label: z.string(),
      pattern: z.string(),
      /** die Nummer, die als nächste vergeben würde */
      nextNumber: z.string(),
      issued: z.int().nonnegative(),
    }),
  ),
  /** Beispiel für die erste Nummer eines neuen Zählers mit dem aktuellen Muster */
  example: z.string(),
  version: z.int(),
})

export const numberRangeList = defineContract({
  name: 'platform.numberRange.list',
  title: 'Nummernkreise anzeigen',
  description: 'Die Nummernkreise des Betriebs mit Muster, begonnenen Zählern und der jeweils nächsten Nummer.',
  llm: {
    description:
      'Liefert die Nummernkreise (Rechnungen, Lieferscheine, Kunden …) mit Muster und der nächsten Nummer je Zähler. ' +
      'Verwenden für „Welche Rechnungsnummer kommt als nächste?“. Nur lesen – Nummern vergibt allein das Festschreiben eines Belegs.',
    keywords: ['Nummernkreis', 'Rechnungsnummer', 'Belegnummer', 'nächste Nummer', 'Muster'],
  },
  input: z.strictObject({}),
  output: z.object({ ranges: z.array(zNumberRange) }),
  permission: 'platform.settings.read',
  risk: 'read',
})

export const numberRangeUpdate = defineContract({
  name: 'platform.numberRange.update',
  title: 'Muster eines Nummernkreises ändern',
  description:
    'Ändert das Muster eines Nummernkreises – nur nach vorn: Bereits begonnene Zähler laufen mit ihrem Muster weiter, das neue gilt für Zähler, die neu entstehen (nächstes Geschäftsjahr, neuer Standort).',
  llm: { description: 'Ändert das Muster eines Nummernkreises. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    id: zId,
    pattern: z.string().trim().min(3, { error: 'Bitte ein Muster angeben, zum Beispiel RE-{YYYY}-{000000}.' }).max(60),
    expectedVersion: z.int().positive().optional(),
  }),
  output: z.object({ id: zId, pattern: z.string(), example: z.string() }),
  permission: 'platform.settings.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

// ---- Zusatzfelder ---------------------------------------------------------------------------------------------------------

export const CUSTOM_FIELD_ENTITIES = ['item', 'partner', 'document', 'document_line', 'lot', 'location'] as const
export const CUSTOM_FIELD_ENTITY_LABELS: Record<(typeof CUSTOM_FIELD_ENTITIES)[number], string> = {
  item: 'Artikel',
  partner: 'Kunden und Lieferanten',
  document: 'Belege',
  document_line: 'Belegpositionen',
  lot: 'Chargen',
  location: 'Standorte',
}
export const CUSTOM_FIELD_TYPES = ['text', 'number', 'integer', 'boolean', 'date', 'select', 'multiselect'] as const
export const CUSTOM_FIELD_TYPE_LABELS: Record<(typeof CUSTOM_FIELD_TYPES)[number], string> = {
  text: 'Text',
  number: 'Zahl',
  integer: 'Ganze Zahl',
  boolean: 'Ja/Nein',
  date: 'Datum',
  select: 'Auswahl',
  multiselect: 'Mehrfachauswahl',
}

export const zCustomFieldDef = z.object({
  id: zId,
  entityType: z.enum(CUSTOM_FIELD_ENTITIES),
  key: z.string(),
  label: z.string(),
  type: z.enum(CUSTOM_FIELD_TYPES),
  required: z.boolean(),
  options: z.array(z.object({ value: z.string(), label: z.string() })),
  showIn: z.array(z.string()),
  sort: z.int(),
  active: z.boolean(),
  version: z.int(),
})

export const customFieldAdminList = defineContract({
  name: 'platform.customField.definitions',
  title: 'Zusatzfelder verwalten: Liste',
  description: 'Alle Zusatzfelder einer Art von Datensatz – auch stillgelegte – in Anzeigereihenfolge.',
  llm: { description: 'Listet Zusatzfelder zur Pflege. Nur für die Oberfläche der Verwaltung; zum Lesen platform.customField.list.', keywords: [] },
  input: z.strictObject({ entityType: z.enum(CUSTOM_FIELD_ENTITIES) }),
  output: z.object({ fields: z.array(zCustomFieldDef) }),
  permission: 'platform.settings.read',
  risk: 'read',
  assistant: 'never',
})

export const customFieldSave = defineContract({
  name: 'platform.customField.save',
  title: 'Zusatzfeld speichern',
  description:
    'Legt ein Zusatzfeld an oder ändert Beschriftung, Pflicht, Auswahlwerte, Reihenfolge und Zustand. Schlüssel und Typ eines bestehenden Felds bleiben, damit gespeicherte Werte lesbar bleiben.',
  llm: { description: 'Speichert ein Zusatzfeld. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    /** ohne Angabe: neues Feld */
    id: zId.optional(),
    entityType: z.enum(CUSTOM_FIELD_ENTITIES),
    /** technischer Schlüssel (a–z, 0–9, _) – nur beim Anlegen */
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_]{1,39}$/, { error: 'Der Schlüssel besteht aus Kleinbuchstaben, Ziffern und Unterstrichen (2 bis 40 Zeichen).' })
      .optional(),
    label: z.string().trim().min(1, { error: 'Bitte geben Sie eine Beschriftung ein.' }).max(80),
    type: z.enum(CUSTOM_FIELD_TYPES).optional(),
    required: z.boolean().default(false),
    options: z
      .array(z.strictObject({ value: z.string().trim().min(1).max(60), label: z.string().trim().min(1).max(80) }))
      .max(100)
      .default([]),
    showIn: z.array(z.enum(['list', 'form', 'document', 'export'])).max(4).default(['form']),
    sort: z.int().min(0).max(100_000).optional(),
    active: z.boolean().default(true),
    expectedVersion: z.int().positive().optional(),
  }),
  output: z.object({ id: zId, version: z.int() }),
  permission: 'platform.settings.manage',
  risk: 'write',
  assistant: 'never',
})
